import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Tag, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDateTime } from '@outlaw-oasis/utils';
import {
  TicketStatusBadge,
  TicketPriorityBadge,
  TicketMessages,
  TicketReplyForm,
} from '../../components/support';

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get(`/support/tickets/${id}`),
    enabled: !!id,
  });

  const ticket = (data as any)?.data?.ticket;
  const messages = (data as any)?.data?.messages || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-surface rounded-xl border border-gold/20 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-hot mx-auto mb-4" />
            <h2 className="text-xl font-medium text-cream mb-2">Ticket Not Found</h2>
            <p className="text-text-secondary mb-4">
              The ticket you're looking for doesn't exist or you don't have access to it.
            </p>
            <Link
              to="/support"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isClosed = ticket.status === 'closed';

  return (
    <div className="min-h-screen bg-charcoal">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back link */}
        <Link
          to="/support"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-cream mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Support
        </Link>

        {/* Ticket Header */}
        <div className="bg-surface rounded-xl border border-gold/20 p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <span className="text-sm font-mono text-gold">{ticket.ticketNumber}</span>
              <h1 className="text-2xl font-western text-cream mt-1">{ticket.subject}</h1>
            </div>
            <div className="flex items-center gap-2">
              <TicketStatusBadge status={ticket.status} />
              <TicketPriorityBadge priority={ticket.priority} showIcon />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
            <div className="flex items-center gap-1">
              <Tag className="w-4 h-4" />
              {ticket.category?.name}
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Created {formatDateTime(ticket.createdAt)}
            </div>
          </div>

          {/* Original description */}
          <div className="mt-4 pt-4 border-t border-gold/10">
            <h3 className="text-sm font-medium text-gold mb-2">Original Description</h3>
            <p className="text-cream whitespace-pre-wrap">{ticket.description}</p>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-surface rounded-xl border border-gold/20 p-6 mb-6">
          <h2 className="text-lg font-western text-gold mb-4">Conversation</h2>
          <TicketMessages messages={messages} />
        </div>

        {/* Reply Form */}
        <div className="bg-surface rounded-xl border border-gold/20 p-6">
          <h2 className="text-lg font-western text-gold mb-4">
            {isClosed ? 'Ticket Closed' : 'Reply'}
          </h2>
          <TicketReplyForm ticketId={ticket.id} disabled={isClosed} />
        </div>
      </div>
    </div>
  );
}
